import { httpService } from './httpService';

export interface RMCDashboardStats {
  totalClients: number;
  totalProposals: number;
  totalUsers: number;
  totalBookings: number;
}

export interface RMCRecentActivity {
  id: string;
  type: string;
  action: string;
  entityName: string;
  userName: string;
  createdDate: string;
  icon: string;
  color: string;
}

export interface RMCTopClient {
  id: string;
  name: string;
  contactEmail: string;
  totalProposals: number;
  activeBookings: number;
  totalRevenue: number;
  createdDate: string;
}

export interface RMCMonthlyStats {
  month: string;
  newClients: number;
  newProposals: number;
  newBookings: number;
  revenue: number;
}

export interface RMCInfo {
  id: string;
  name: string;
  cityName: string;
  address: string;
  contactEmail: string;
  contactPhone: string;
}

export interface RMCDashboard {
  stats: RMCDashboardStats;
  recentActivities: RMCRecentActivity[];
  topClients: RMCTopClient[];
  monthlyStats: RMCMonthlyStats[];
  rmcInfo: RMCInfo;
}

class RMCDashboardService {
  // Keep relative so httpService baseURL is applied
  private baseUrl = '/api/rmc-dashboard';

  async getRMCDashboard(rmcId: number): Promise<RMCDashboard> {
    try {
      const data = await httpService.get<RMCDashboard>(`${this.baseUrl}/${rmcId}/admin`, undefined, { unwrap: true });
      return data;
    } catch (e) {
      console.error('Failed fetching RMC dashboard:', e);
      throw e;
    }
  }

  async getRMCDashboardStats(rmcId: number): Promise<RMCDashboardStats> {
    return await httpService.get<RMCDashboardStats>(`${this.baseUrl}/${rmcId}/stats`, undefined, { unwrap: true });
  }

  async getRMCRecentActivities(rmcId: number, count: number = 10): Promise<RMCRecentActivity[]> {
    return await httpService.get<RMCRecentActivity[]>(`${this.baseUrl}/${rmcId}/activities?count=${count}`, undefined, { unwrap: true });
  }

  async getRMCTopClients(rmcId: number, count: number = 5): Promise<RMCTopClient[]> {
    return await httpService.get<RMCTopClient[]>(`${this.baseUrl}/${rmcId}/top-clients?count=${count}`, undefined, { unwrap: true });
  }

  async getRMCMonthlyStats(rmcId: number, months: number = 12): Promise<RMCMonthlyStats[]> {
    return await httpService.get<RMCMonthlyStats[]>(`${this.baseUrl}/${rmcId}/monthly-stats?months=${months}`, undefined, { unwrap: true });
  }

  async getRMCInfo(rmcId: number): Promise<RMCInfo> {
    return await httpService.get<RMCInfo>(`${this.baseUrl}/${rmcId}/info`, undefined, { unwrap: true });
  }
}

export const rmcDashboardService = new RMCDashboardService();
